import React, { useState } from 'react';
import { TrendingUp, TrendingDown, MousePointer, DollarSign, Users, Eye, Target, ThumbsUp, MessageSquare, Bookmark, Share2, BarChart3, X } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const RealEstateManagingPartnerMarketing = () => {
  const [selectedModal, setSelectedModal] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [activeLeadTab, setActiveLeadTab] = useState('leadQuality');

  const openModal = (type, data) => {
    if (typeof type === 'string' && !data) {
      // Called from widget
      console.log('Opening modal:', type);
    } else {
      // Original modal functionality
      setSelectedModal(type);
      setModalData(data);
    }
  };

  const closeModal = () => {
    setSelectedModal(null);
    setModalData(null);
  };

  // Trends data for chart
  const trendsData = [
    { date: 'Jun 1', traffic: 12500, roi: 285, conversions: 142 },
    { date: 'Jun 8', traffic: 14200, roi: 298, conversions: 156 },
    { date: 'Jun 15', traffic: 15800, roi: 312, conversions: 168 },
    { date: 'Jun 22', traffic: 16500, roi: 305, conversions: 174 },
    { date: 'Jun 29', traffic: 17200, roi: 318, conversions: 182 },
    { date: 'Jul 6', traffic: 18500, roi: 325, conversions: 195 },
    { date: 'Jul 13', traffic: 19800, roi: 342, conversions: 208 },
    { date: 'Jul 20', traffic: 21200, roi: 335, conversions: 218 },
    { date: 'Jul 27', traffic: 22500, roi: 348, conversions: 232 },
    { date: 'Aug 3', traffic: 24100, roi: 365, conversions: 245 },
    { date: 'Aug 10', traffic: 25800, roi: 378, conversions: 258 },
    { date: 'Aug 17', traffic: 27500, roi: 392, conversions: 275 }
  ];

  // Lead quality data
  const leadQualityData = [
    { 
      date: 'Today, 14:12', 
      company: 'Manhattan Properties LLC', 
      email: 'contact@manhattanprop.com', 
      source: 'Google Ads',
      quality: 'HIGH POTENTIAL', 
      country: 'USA',
      services: 'Commercial Office Space',
      referrer: 'Google Ads (Summer Campaign)',
      revenue: '$2.8M',
      aiScore: 92
    },
    { 
      date: 'Today, 11:45', 
      company: 'Brooklyn Development Co', 
      email: 'info@brooklyndev.com', 
      source: 'Social Media',
      quality: 'NEEDS REVIEW', 
      country: 'USA',
      services: 'Mixed-Use Development',
      referrer: 'LinkedIn Campaign',
      revenue: '$1.5M',
      aiScore: 68
    },
    { 
      date: 'Aug 17, 16:22', 
      company: 'Uptown Realty Group', 
      email: 'sales@uptownrealty.com', 
      source: 'Email Campaign',
      quality: 'GOOD', 
      country: 'USA',
      services: 'Luxury Residential',
      referrer: 'Email Newsletter',
      revenue: '$3.2M',
      aiScore: 85
    },
    { 
      date: 'Aug 17, 14:08', 
      company: 'Queens Commercial LLC', 
      email: 'business@queenscom.com', 
      source: 'Organic Search',
      quality: 'NEUTRAL', 
      country: 'USA',
      services: 'Retail Space',
      referrer: 'Google Organic',
      revenue: '$850K',
      aiScore: 55
    },
    { 
      date: 'Aug 16, 18:15', 
      company: 'Bronx Investors Group', 
      email: 'invest@bronxinv.com', 
      source: 'Referral',
      quality: 'HIGH POTENTIAL', 
      country: 'USA',
      services: 'Industrial Property',
      referrer: 'Partner Referral',
      revenue: '$4.1M',
      aiScore: 94
    }
  ];

  // Day on Day comparison data
  const dayOnDayData = [
    { metric: 'Total Leads', today: 42, yesterday: 38, change: '+10.5%', trend: 'up' },
    { metric: 'Qualified Leads', today: 28, yesterday: 25, change: '+12.0%', trend: 'up' },
    { metric: 'Conversion Rate', today: '66.7%', yesterday: '65.8%', change: '+0.9%', trend: 'up' },
    { metric: 'Avg. Lead Score', today: 78, yesterday: 74, change: '+5.4%', trend: 'up' },
    { metric: 'Response Time', today: '2.3h', yesterday: '3.1h', change: '-25.8%', trend: 'up' }
  ];

  // Week on Week comparison data
  const weekOnWeekData = [
    { metric: 'Total Leads', thisWeek: 285, lastWeek: 268, change: '+6.3%', trend: 'up' },
    { metric: 'Qualified Leads', thisWeek: 192, lastWeek: 178, change: '+7.9%', trend: 'up' },
    { metric: 'Conversion Rate', thisWeek: '67.4%', lastWeek: '66.4%', change: '+1.0%', trend: 'up' },
    { metric: 'Total Revenue', thisWeek: '$12.5M', lastWeek: '$11.2M', change: '+11.6%', trend: 'up' },
    { metric: 'Cost per Lead', thisWeek: '$3,240', lastWeek: '$3,580', change: '-9.5%', trend: 'up' }
  ];

  // SQR (Sales Qualified Rate) data
  const sqrData = [
    { source: 'Google Ads', totalLeads: 142, qualified: 95, sqr: '66.9%', revenue: '$5.8M' },
    { source: 'LinkedIn', totalLeads: 89, qualified: 72, sqr: '80.9%', revenue: '$4.2M' },
    { source: 'Organic Search', totalLeads: 67, qualified: 38, sqr: '56.7%', revenue: '$2.1M' },
    { source: 'Email Campaign', totalLeads: 58, qualified: 42, sqr: '72.4%', revenue: '$3.5M' },
    { source: 'Referral', totalLeads: 34, qualified: 31, sqr: '91.2%', revenue: '$6.2M' }
  ];

  // Keyword Performance data
  const keywordData = [
    { keyword: 'commercial real estate NYC', impressions: '28.5K', clicks: 2420, ctr: '8.5%', conversions: 68, cpa: '$1,850' },
    { keyword: 'office space Manhattan', impressions: '22.1K', clicks: 1980, ctr: '9.0%', conversions: 54, cpa: '$2,120' },
    { keyword: 'luxury apartments Brooklyn', impressions: '18.7K', clicks: 1650, ctr: '8.8%', conversions: 42, cpa: '$1,980' },
    { keyword: 'industrial property NYC', impressions: '15.3K', clicks: 1280, ctr: '8.4%', conversions: 38, cpa: '$2,340' },
    { keyword: 'retail space lease', impressions: '12.8K', clicks: 1050, ctr: '8.2%', conversions: 29, cpa: '$2,580' }
  ];

  // SEO Performance data
  const seoPerformanceData = [
    { month: 'Feb', organicTraffic: 12500, keywords: 245, backlinks: 1240, domainRating: 58 },
    { month: 'Mar', organicTraffic: 14800, keywords: 268, backlinks: 1380, domainRating: 61 },
    { month: 'Apr', organicTraffic: 16200, keywords: 289, backlinks: 1520, domainRating: 63 },
    { month: 'May', organicTraffic: 18600, keywords: 312, backlinks: 1680, domainRating: 65 },
    { month: 'Jun', organicTraffic: 21200, keywords: 340, backlinks: 1850, domainRating: 68 },
    { month: 'Jul', organicTraffic: 24500, keywords: 378, backlinks: 2120, domainRating: 71 },
    { month: 'Aug', organicTraffic: 27500, keywords: 412, backlinks: 2380, domainRating: 74 }
  ];

  // Instagram data
  const instagramData = {
    followers: 135145,
    newFollowers: 16,
    interactions: 1356,
    newInteractions: 7,
    metrics: [
      { name: 'Likes', value: 892 },
      { name: 'Comments', value: 234 },
      { name: 'Saves', value: 145 },
      { name: 'Shares', value: 85 }
    ],
    topPosts: [
      { id: 1, date: 'Aug 15', image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200', likes: 428 },
      { id: 2, date: 'Aug 12', image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=200', likes: 385 },
      { id: 3, date: 'Aug 9', image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=200', likes: 362 }
    ]
  };

  // X.com data with daily interactions
  const xInteractionsData = [
    { day: '27', interactions: 145 },
    { day: '28', interactions: 168 },
    { day: '29', interactions: 152 },
    { day: '30', interactions: 189 },
    { day: '31', interactions: 195 },
    { day: '1', interactions: 210 },
    { day: '2', interactions: 225 }
  ];

  const getQualityColor = (quality) => {
    switch (quality) {
      case 'HIGH POTENTIAL': return 'bg-green-500/20 text-green-500';
      case 'GOOD': return 'bg-blue-500/20 text-blue-500';
      case 'NEEDS REVIEW': return 'bg-orange-500/20 text-orange-500';
      case 'NEUTRAL': return 'bg-gray-500/20 text-gray-500';
      default: return 'bg-gray-500/20 text-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden overflow-y-auto">
      {/* Header */}
      <div className="px-4 py-4 md:px-6 md:py-6 mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Marketing Analytics</h1>
        <p className="text-gray-400 text-sm md:text-base">Commercial Real Estate Marketing Performance</p>
      </div>

      {/* Content Container with proper padding */}
      <div className="px-4 md:px-6">
      {/* Top KPI Metrics - Responsive grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8">
        {/* Traffic */}
        <div 
          className="bg-black border border-gray-400 rounded-xl p-4 md:p-5 cursor-pointer hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 min-h-[100px]"
          onClick={() => openModal('metric', {
            title: 'Website Traffic',
            value: '27.5K',
            change: '+3.2%',
            description: 'Total website visitors across all marketing channels. Traffic has grown consistently with peak engagement from LinkedIn and Google Ads campaigns.',
            breakdown: [
              { source: 'Google Ads', value: '12.5K', percentage: '45%' },
              { source: 'Organic Search', value: '8.2K', percentage: '30%' },
              { source: 'Social Media', value: '4.8K', percentage: '17%' },
              { source: 'Direct', value: '2.0K', percentage: '8%' }
            ]
          })}
        >
          <div className="flex items-center gap-2 mb-2">
            <Eye size={16} className="text-red-500" />
            <div className="text-xs text-gray-400 uppercase tracking-wide">Traffic</div>
          </div>
          <div className="text-2xl md:text-3xl font-bold mb-1">27.5K</div>
          <div className="flex items-center gap-1">
            <TrendingUp size={12} className="text-green-500" />
            <span className="text-xs text-green-500 font-medium">+3.2%</span>
          </div>
        </div>

        {/* ROI */}
        <div 
          className="bg-black border border-gray-400 rounded-xl p-4 md:p-5 cursor-pointer hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 min-h-[100px]"
          onClick={() => openModal('metric', {
            title: 'Return on Investment',
            value: '392%',
            change: '+4.1%',
            description: 'Marketing ROI calculated based on revenue generated vs marketing spend. Strong performance driven by high-value commercial property deals.',
            breakdown: [
              { source: 'Total Revenue', value: '$28.5M', percentage: '100%' },
              { source: 'Marketing Spend', value: '$7.2M', percentage: '25%' },
              { source: 'Net Profit', value: '$21.3M', percentage: '75%' },
              { source: 'ROI', value: '392%', percentage: '-' }
            ]
          })}
        >
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={16} className="text-blue-500" />
            <div className="text-xs text-gray-400 uppercase tracking-wide">ROI</div>
          </div>
          <div className="text-2xl md:text-3xl font-bold mb-1">392%</div>
          <div className="flex items-center gap-1">
            <TrendingUp size={12} className="text-green-500" />
            <span className="text-xs text-green-500 font-medium">+4.1%</span>
          </div>
        </div>

        {/* Conversions */}
        <div 
          className="bg-black border border-gray-400 rounded-xl p-4 md:p-5 cursor-pointer hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 min-h-[100px]"
          onClick={() => openModal('metric', {
            title: 'Total Conversions',
            value: '275',
            change: '-2.7%',
            description: 'Total qualified leads converted to signed contracts. Slight decrease due to longer sales cycles in commercial real estate.',
            breakdown: [
              { source: 'Office Space', value: '125', percentage: '45%' },
              { source: 'Retail', value: '72', percentage: '26%' },
              { source: 'Industrial', value: '48', percentage: '17%' },
              { source: 'Mixed-Use', value: '30', percentage: '12%' }
            ]
          })}
        >
          <div className="flex items-center gap-2 mb-2">
            <Target size={16} className="text-gray-400" />
            <div className="text-xs text-gray-400 uppercase tracking-wide">Conversions</div>
          </div>
          <div className="text-2xl md:text-3xl font-bold mb-1">275</div>
          <div className="flex items-center gap-1">
            <TrendingDown size={12} className="text-red-500" />
            <span className="text-xs text-red-500 font-medium">-2.7%</span>
          </div>
        </div>

        {/* Expenses */}
        <div 
          className="bg-black border border-gray-400 rounded-xl p-4 md:p-5 cursor-pointer hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 min-h-[100px]"
          onClick={() => openModal('metric', {
            title: 'Marketing Expenses',
            value: '$7.2M',
            change: '-2.0%',
            description: 'Total marketing budget spent this quarter. Cost optimization strategies have reduced expenses while maintaining lead quality.',
            breakdown: [
              { source: 'Digital Ads', value: '$2.8M', percentage: '39%' },
              { source: 'Events & Sponsorships', value: '$1.8M', percentage: '25%' },
              { source: 'Content Production', value: '$1.4M', percentage: '19%' },
              { source: 'Social Media', value: '$1.2M', percentage: '17%' }
            ]
          })}
        >
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={16} className="text-purple-500" />
            <div className="text-xs text-gray-400 uppercase tracking-wide">Expenses</div>
          </div>
          <div className="text-2xl md:text-3xl font-bold mb-1">$7.2M</div>
          <div className="flex items-center gap-1">
            <TrendingDown size={12} className="text-green-500" />
            <span className="text-xs text-green-500 font-medium">-2.0%</span>
          </div>
        </div>

        {/* CTR */}
        <div 
          className="bg-black border border-gray-400 rounded-xl p-4 md:p-5 cursor-pointer hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 min-h-[100px]"
          onClick={() => openModal('metric', {
            title: 'Click-Through Rate',
            value: '8.4%',
            change: '-0.2%',
            description: 'Average click-through rate across all digital advertising campaigns. Industry-leading performance for commercial real estate sector.',
            breakdown: [
              { source: 'Search Ads', value: '12.5%', percentage: 'High' },
              { source: 'Display Ads', value: '6.8%', percentage: 'Medium' },
              { source: 'Social Ads', value: '9.2%', percentage: 'High' },
              { source: 'Email', value: '5.4%', percentage: 'Medium' }
            ]
          })}
        >
          <div className="flex items-center gap-2 mb-2">
            <MousePointer size={16} className="text-yellow-500" />
            <div className="text-xs text-gray-400 uppercase tracking-wide">CTR</div>
          </div>
          <div className="text-2xl md:text-3xl font-bold mb-1">8.4%</div>
          <div className="flex items-center gap-1">
            <TrendingDown size={12} className="text-red-500" />
            <span className="text-xs text-red-500 font-medium">-0.2%</span>
          </div>
        </div>

        {/* CPA */}
        <div 
          className="bg-black border border-gray-400 rounded-xl p-4 md:p-5 cursor-pointer hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 min-h-[100px]"
          onClick={() => openModal('metric', {
            title: 'Cost Per Acquisition',
            value: '$26.2K',
            change: '-$1.8K',
            description: 'Average cost to acquire one qualified client. Decreased due to improved targeting and higher conversion rates on premium listings.',
            breakdown: [
              { source: 'Premium Listings', value: '$18.5K', percentage: 'Best' },
              { source: 'Standard Listings', value: '$28.4K', percentage: 'Good' },
              { source: 'Budget Campaigns', value: '$35.2K', percentage: 'Fair' },
              { source: 'Overall Average', value: '$26.2K', percentage: '-' }
            ]
          })}
        >
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-teal-500" />
            <div className="text-xs text-gray-400 uppercase tracking-wide">CPA</div>
          </div>
          <div className="text-2xl md:text-3xl font-bold mb-1">$26.2K</div>
          <div className="flex items-center gap-1">
            <TrendingDown size={12} className="text-green-500" />
            <span className="text-xs text-green-500 font-medium">-$1.8K</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
        {/* Trends Chart - Takes 2 columns on desktop */}
        <div className="lg:col-span-2 bg-black border border-gray-400 rounded-xl p-4 md:p-6 pb-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
            <h3 className="text-lg md:text-xl font-bold">Performance Trends</h3>
            <div className="flex items-center gap-3 md:gap-4 flex-wrap">
              <div className="flex items-center gap-1.5 md:gap-2">
                <div className="w-2 h-2 md:w-3 md:h-3 bg-red-500 rounded-full"></div>
                <span className="text-[10px] md:text-xs text-gray-400">Traffic</span>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2">
                <div className="w-2 h-2 md:w-3 md:h-3 bg-blue-500 rounded-full"></div>
                <span className="text-[10px] md:text-xs text-gray-400">ROI</span>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2">
                <div className="w-2 h-2 md:w-3 md:h-3 bg-gray-400 rounded-full"></div>
                <span className="text-[10px] md:text-xs text-gray-400">Conversions</span>
              </div>
            </div>
          </div>
          
          <ResponsiveContainer width="100%" height={200} className="md:hidden">
            <LineChart data={trendsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 8 }} interval={2} />
              <YAxis yAxisId="left" stroke="#666" tick={{ fontSize: 8 }} width={35} />
              <YAxis yAxisId="right" orientation="right" stroke="#666" tick={{ fontSize: 8 }} width={35} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px', fontSize: '10px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Line yAxisId="left" type="monotone" dataKey="traffic" stroke="#EF4444" strokeWidth={2} name="Traffic" dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="roi" stroke="#3B82F6" strokeWidth={2} name="ROI" dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="conversions" stroke="#9CA3AF" strokeWidth={2} name="Conversions" dot={false} />
            </LineChart>
          </ResponsiveContainer>

          <ResponsiveContainer width="100%" height={280} className="hidden md:block">
            <LineChart data={trendsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" stroke="#666" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" stroke="#666" tick={{ fontSize: 10 }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Line yAxisId="left" type="monotone" dataKey="traffic" stroke="#EF4444" strokeWidth={2} name="Traffic" />
              <Line yAxisId="right" type="monotone" dataKey="roi" stroke="#3B82F6" strokeWidth={2} name="ROI" />
              <Line yAxisId="right" type="monotone" dataKey="conversions" stroke="#9CA3AF" strokeWidth={2} name="Conversions" />
            </LineChart>
          </ResponsiveContainer>

          {/* SEO Performance Chart */}
          <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-400/20">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
              <div>
                <h3 className="text-lg md:text-xl font-bold">SEO Performance</h3>
                <p className="text-xs md:text-sm text-gray-400">Organic growth metrics and rankings</p>
              </div>
              <div className="flex items-center gap-3 md:gap-4 flex-wrap">
                <div className="flex items-center gap-1.5 md:gap-2">
                  <div className="w-2 h-2 md:w-3 md:h-3 bg-emerald-500 rounded-full"></div>
                  <span className="text-[10px] md:text-xs text-gray-400">Organic</span>
                </div>
                <div className="flex items-center gap-1.5 md:gap-2">
                  <div className="w-2 h-2 md:w-3 md:h-3 bg-purple-500 rounded-full"></div>
                  <span className="text-[10px] md:text-xs text-gray-400">Keywords</span>
                </div>
                <div className="flex items-center gap-1.5 md:gap-2">
                  <div className="w-2 h-2 md:w-3 md:h-3 bg-amber-500 rounded-full"></div>
                  <span className="text-[10px] md:text-xs text-gray-400">Backlinks</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4">
              <div className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/30 border border-emerald-500/50 rounded-lg p-2 md:p-3">
                <div className="text-[10px] md:text-xs text-gray-400 mb-0.5 md:mb-1">Organic Traffic</div>
                <div className="text-base md:text-xl font-bold text-emerald-400">27.5K</div>
                <div className="flex items-center gap-1 mt-0.5 md:mt-1">
                  <TrendingUp size={10} className="text-emerald-500" />
                  <span className="text-[10px] md:text-xs text-emerald-500">+32.1%</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/30 border border-purple-500/50 rounded-lg p-2 md:p-3">
                <div className="text-[10px] md:text-xs text-gray-400 mb-0.5 md:mb-1">Keywords</div>
                <div className="text-base md:text-xl font-bold text-purple-400">412</div>
                <div className="flex items-center gap-1 mt-0.5 md:mt-1">
                  <TrendingUp size={10} className="text-purple-500" />
                  <span className="text-[10px] md:text-xs text-purple-500">+21.2%</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/30 border border-amber-500/50 rounded-lg p-2 md:p-3">
                <div className="text-[10px] md:text-xs text-gray-400 mb-0.5 md:mb-1">Backlinks</div>
                <div className="text-base md:text-xl font-bold text-amber-400">2,380</div>
                <div className="flex items-center gap-1 mt-0.5 md:mt-1">
                  <TrendingUp size={10} className="text-amber-500" />
                  <span className="text-[10px] md:text-xs text-amber-500">+28.6%</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 border border-blue-500/50 rounded-lg p-2 md:p-3">
                <div className="text-[10px] md:text-xs text-gray-400 mb-0.5 md:mb-1">Domain Rating</div>
                <div className="text-base md:text-xl font-bold text-blue-400">74</div>
                <div className="flex items-center gap-1 mt-0.5 md:mt-1">
                  <TrendingUp size={10} className="text-blue-500" />
                  <span className="text-[10px] md:text-xs text-blue-500">+27.6%</span>
                </div>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={200} className="md:hidden">
              <LineChart data={seoPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#666" tick={{ fontSize: 8 }} />
                <YAxis yAxisId="left" stroke="#666" tick={{ fontSize: 8 }} width={35} />
                <YAxis yAxisId="right" orientation="right" stroke="#666" tick={{ fontSize: 8 }} width={35} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px', fontSize: '10px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line yAxisId="left" type="monotone" dataKey="organicTraffic" stroke="#10B981" strokeWidth={2} name="Organic" dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="keywords" stroke="#A855F7" strokeWidth={2} name="Keywords" dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="backlinks" stroke="#F59E0B" strokeWidth={2} name="Backlinks" dot={false} />
              </LineChart>
            </ResponsiveContainer>

            <ResponsiveContainer width="100%" height={280} className="hidden md:block">
              <LineChart data={seoPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#666" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" stroke="#666" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" stroke="#666" tick={{ fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line yAxisId="left" type="monotone" dataKey="organicTraffic" stroke="#10B981" strokeWidth={2} name="Organic Traffic" />
                <Line yAxisId="right" type="monotone" dataKey="keywords" stroke="#A855F7" strokeWidth={2} name="Keywords" />
                <Line yAxisId="right" type="monotone" dataKey="backlinks" stroke="#F59E0B" strokeWidth={2} name="Backlinks" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Sidebar - Social Media */}
        <div className="space-y-4 md:space-y-6">
          {/* Instagram Card */}
          <div className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Instagram</h3>
            
            <div className="mb-3 md:mb-4">
              <div className="flex items-center justify-between mb-0.5 md:mb-1">
                <span className="text-gray-400 text-xs md:text-sm">Followers</span>
                <span className="text-green-500 text-xs md:text-sm font-semibold">+{instagramData.newFollowers}</span>
              </div>
              <div className="text-xl md:text-2xl font-bold">{instagramData.followers.toLocaleString()}</div>
            </div>

            <div className="mb-3 md:mb-4">
              <div className="flex items-center justify-between mb-0.5 md:mb-1">
                <span className="text-gray-400 text-xs md:text-sm">Interactions</span>
                <span className="text-green-500 text-xs md:text-sm font-semibold">+{instagramData.newInteractions}</span>
              </div>
              <div className="text-lg md:text-xl font-bold">{instagramData.interactions.toLocaleString()}</div>
            </div>

            <div className="space-y-1.5 md:space-y-2 mb-3 md:mb-4">
              {instagramData.metrics.map((metric, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs md:text-sm">
                  <span className="text-gray-400">{metric.name}</span>
                  <span className="text-white font-semibold">{metric.value}</span>
                </div>
              ))}
            </div>

            <div>
              <div className="text-xs md:text-sm text-gray-400 mb-2">Top Posts</div>
              <div className="grid grid-cols-3 gap-1.5 md:gap-2">
                {instagramData.topPosts.map((post) => (
                  <div key={post.id} className="cursor-pointer hover:opacity-80 transition-opacity">
                    <img src={post.image} alt="Post" className="w-full h-14 md:h-20 object-cover rounded" />
                    <div className="text-[10px] md:text-xs text-gray-400 mt-0.5 md:mt-1">{post.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* X.com Card */}
          <div className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">X.com</h3>
            
            <div className="mb-3 md:mb-4">
              <div className="flex items-center justify-between mb-0.5 md:mb-1">
                <span className="text-gray-400 text-xs md:text-sm">Followers</span>
                <span className="text-green-500 text-xs md:text-sm font-semibold">+7</span>
              </div>
              <div className="text-xl md:text-2xl font-bold">45,015</div>
            </div>

            <div className="mb-3 md:mb-4">
              <ResponsiveContainer width="100%" height={60} className="md:hidden">
                <BarChart data={xInteractionsData}>
                  <XAxis dataKey="day" stroke="#666" tick={{ fontSize: 8 }} />
                  <Bar dataKey="interactions" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
              <ResponsiveContainer width="100%" height={80} className="hidden md:block">
                <BarChart data={xInteractionsData}>
                  <XAxis dataKey="day" stroke="#666" tick={{ fontSize: 10 }} />
                  <Bar dataKey="interactions" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-2 md:space-y-3">
              <div>
                <div className="text-[10px] md:text-xs text-gray-400 mb-0.5 md:mb-1">Impressions</div>
                <div className="flex items-center justify-between">
                  <div className="text-base md:text-lg font-bold">128.5K</div>
                  <div className="flex items-center gap-1">
                    <TrendingUp size={10} className="text-green-500" />
                    <span className="text-[10px] md:text-xs text-green-500">+12%</span>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[10px] md:text-xs text-gray-400 mb-0.5 md:mb-1">Engagement Rate</div>
                <div className="flex items-center justify-between">
                  <div className="text-base md:text-lg font-bold">15.8%</div>
                  <div className="flex items-center gap-1">
                    <TrendingUp size={10} className="text-green-500" />
                    <span className="text-[10px] md:text-xs text-green-500">+82%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Quality Table with Tabs */}
      <div className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
        {/* Tabs - Scrollable on mobile */}
        <div className="flex items-center gap-1 mb-4 md:mb-6 border-b border-gray-400/20 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setActiveLeadTab('leadQuality')}
            className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeLeadTab === 'leadQuality'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Lead Quality
          </button>
          <button
            onClick={() => setActiveLeadTab('dayOnDay')}
            className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeLeadTab === 'dayOnDay'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Day on Day
          </button>
          <button
            onClick={() => setActiveLeadTab('weekOnWeek')}
            className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeLeadTab === 'weekOnWeek'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Week on Week
          </button>
          <button
            onClick={() => setActiveLeadTab('sqr')}
            className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeLeadTab === 'sqr'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            SQR
          </button>
          <button
            onClick={() => setActiveLeadTab('keywordPerformance')}
            className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold transition-colors whitespace-nowrap ${
              activeLeadTab === 'keywordPerformance'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Keywords
          </button>
        </div>

        {/* Tab Content */}
        <div className="overflow-x-auto">
          {/* Lead Quality Tab */}
          {activeLeadTab === 'leadQuality' && (
            <>
              {/* Desktop Table */}
              <table className="w-full hidden md:table">
                <thead>
                  <tr className="border-b border-gray-400">
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">DATE</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">COMPANY</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">EMAIL</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">SOURCE</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">LEAD QUALITY</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">COUNTRY</th>
                  </tr>
                </thead>
                <tbody>
                  {leadQualityData.map((lead, idx) => (
                    <tr 
                      key={idx} 
                      className="border-b border-gray-800 hover:bg-white/5 cursor-pointer"
                      onClick={() => openModal('lead', lead)}
                    >
                      <td className="py-4 px-2 text-white text-sm">{lead.date}</td>
                      <td className="py-4 px-2 text-white font-semibold text-sm">{lead.company}</td>
                      <td className="py-4 px-2 text-gray-400 text-sm">{lead.email}</td>
                      <td className="py-4 px-2 text-white text-sm">{lead.source}</td>
                      <td className="py-4 px-2">
                        <span className={`px-3 py-1 rounded text-xs font-semibold ${getQualityColor(lead.quality)}`}>
                          {lead.quality}
                        </span>
                      </td>
                      <td className="py-4 px-2 text-white text-sm">{lead.country}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {leadQualityData.map((lead, idx) => (
                  <div 
                    key={idx} 
                    className="bg-gray-900/50 rounded-lg p-3 border border-gray-700 cursor-pointer"
                    onClick={() => openModal('lead', lead)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white truncate">{lead.company}</p>
                        <p className="text-[10px] text-gray-400 truncate">{lead.email}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ml-2 flex-shrink-0 ${getQualityColor(lead.quality)}`}>
                        {lead.quality}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-400">
                      <span>{lead.date}</span>
                      <span>{lead.source}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Day on Day Tab */}
          {activeLeadTab === 'dayOnDay' && (
            <>
              {/* Desktop Table */}
              <table className="w-full hidden md:table">
                <thead>
                  <tr className="border-b border-gray-400">
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">METRIC</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">TODAY</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">YESTERDAY</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">CHANGE</th>
                  </tr>
                </thead>
                <tbody>
                  {dayOnDayData.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-800 hover:bg-white/5">
                      <td className="py-4 px-2 text-white font-semibold text-sm">{item.metric}</td>
                      <td className="py-4 px-2 text-white text-sm">{item.today}</td>
                      <td className="py-4 px-2 text-gray-400 text-sm">{item.yesterday}</td>
                      <td className="py-4 px-2">
                        <span className={`flex items-center gap-1 text-sm font-semibold ${item.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                          {item.trend === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          {item.change}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile Cards */}
              <div className="md:hidden space-y-2">
                {dayOnDayData.map((item, idx) => (
                  <div key={idx} className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-white">{item.metric}</span>
                      <span className={`flex items-center gap-1 text-xs font-semibold ${item.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                        {item.trend === 'up' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                        {item.change}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-400">
                      <span>Today: <span className="text-white">{item.today}</span></span>
                      <span>Yesterday: {item.yesterday}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Week on Week Tab */}
          {activeLeadTab === 'weekOnWeek' && (
            <>
              {/* Desktop Table */}
              <table className="w-full hidden md:table">
                <thead>
                  <tr className="border-b border-gray-400">
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">METRIC</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">THIS WEEK</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">LAST WEEK</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">CHANGE</th>
                  </tr>
                </thead>
                <tbody>
                  {weekOnWeekData.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-800 hover:bg-white/5">
                      <td className="py-4 px-2 text-white font-semibold text-sm">{item.metric}</td>
                      <td className="py-4 px-2 text-white text-sm">{item.thisWeek}</td>
                      <td className="py-4 px-2 text-gray-400 text-sm">{item.lastWeek}</td>
                      <td className="py-4 px-2">
                        <span className={`flex items-center gap-1 text-sm font-semibold ${item.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                          {item.trend === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          {item.change}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile Cards */}
              <div className="md:hidden space-y-2">
                {weekOnWeekData.map((item, idx) => (
                  <div key={idx} className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-white">{item.metric}</span>
                      <span className={`flex items-center gap-1 text-xs font-semibold ${item.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                        {item.trend === 'up' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                        {item.change}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-400">
                      <span>This Week: <span className="text-white">{item.thisWeek}</span></span>
                      <span>Last Week: {item.lastWeek}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* SQR Tab */}
          {activeLeadTab === 'sqr' && (
            <>
              {/* Desktop Table */}
              <table className="w-full hidden md:table">
                <thead>
                  <tr className="border-b border-gray-400">
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">SOURCE</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">TOTAL LEADS</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">QUALIFIED</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">SQR</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">REVENUE</th>
                  </tr>
                </thead>
                <tbody>
                  {sqrData.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-800 hover:bg-white/5">
                      <td className="py-4 px-2 text-white font-semibold text-sm">{item.source}</td>
                      <td className="py-4 px-2 text-white text-sm">{item.totalLeads}</td>
                      <td className="py-4 px-2 text-white text-sm">{item.qualified}</td>
                      <td className="py-4 px-2 text-teal-500 font-semibold text-sm">{item.sqr}</td>
                      <td className="py-4 px-2 text-green-500 font-semibold text-sm">{item.revenue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile Cards */}
              <div className="md:hidden space-y-2">
                {sqrData.map((item, idx) => (
                  <div key={idx} className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-white">{item.source}</span>
                      <span className="text-xs font-semibold text-green-500">{item.revenue}</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-400">
                      <span>Leads: <span className="text-white">{item.totalLeads}</span> → <span className="text-white">{item.qualified}</span></span>
                      <span className="text-teal-500 font-semibold">SQR: {item.sqr}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Keyword Performance Tab */}
          {activeLeadTab === 'keywordPerformance' && (
            <>
              {/* Desktop Table */}
              <table className="w-full hidden md:table">
                <thead>
                  <tr className="border-b border-gray-400">
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">KEYWORD</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">IMPRESSIONS</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">CLICKS</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">CTR</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">CONVERSIONS</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">CPA</th>
                  </tr>
                </thead>
                <tbody>
                  {keywordData.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-800 hover:bg-white/5">
                      <td className="py-4 px-2 text-white font-semibold text-sm">{item.keyword}</td>
                      <td className="py-4 px-2 text-white text-sm">{item.impressions}</td>
                      <td className="py-4 px-2 text-white text-sm">{item.clicks}</td>
                      <td className="py-4 px-2 text-blue-500 font-semibold text-sm">{item.ctr}</td>
                      <td className="py-4 px-2 text-white text-sm">{item.conversions}</td>
                      <td className="py-4 px-2 text-purple-500 font-semibold text-sm">{item.cpa}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile Cards */}
              <div className="md:hidden space-y-2">
                {keywordData.map((item, idx) => (
                  <div key={idx} className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                    <p className="text-xs font-semibold text-white mb-2 truncate">{item.keyword}</p>
                    <div className="grid grid-cols-3 gap-2 text-[10px]">
                      <div>
                        <span className="text-gray-400">Impressions</span>
                        <p className="text-white">{item.impressions}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Clicks</span>
                        <p className="text-white">{item.clicks}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">CTR</span>
                        <p className="text-blue-500 font-semibold">{item.ctr}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[10px]">
                      <span className="text-gray-400">Conv: <span className="text-white">{item.conversions}</span></span>
                      <span className="text-purple-500 font-semibold">CPA: {item.cpa}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal Component */}
      {selectedModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 md:p-4" onClick={closeModal}>
          <div className="bg-black border-2 border-gray-400 rounded-xl p-4 md:p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-lg md:text-2xl font-bold truncate pr-2">
                {selectedModal === 'metric' && modalData?.title}
                {selectedModal === 'lead' && `Lead: ${modalData?.company}`}
              </h2>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-white w-8 h-8 flex items-center justify-center flex-shrink-0"
              >
                <X size={20} className="md:hidden" />
                <X size={24} className="hidden md:block" />
              </button>
            </div>

            {/* Metric Modal Content */}
            {selectedModal === 'metric' && (
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="text-2xl md:text-4xl font-bold">{modalData.value}</div>
                  <div className="text-green-500 text-base md:text-xl font-semibold">{modalData.change}</div>
                </div>
                
                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <p className="text-gray-300 text-xs md:text-base">{modalData.description}</p>
                </div>

                <div>
                  <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Breakdown</h3>
                  <div className="space-y-2 md:space-y-3">
                    {modalData.breakdown.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2.5 md:p-3 bg-white/5 border border-gray-400/20 rounded-lg">
                        <div>
                          <div className="text-white font-semibold text-sm md:text-base">{item.source}</div>
                          <div className="text-gray-400 text-[10px] md:text-sm">{item.percentage}</div>
                        </div>
                        <div className="text-base md:text-xl font-bold text-teal-500">{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Lead Modal Content */}
            {selectedModal === 'lead' && (
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Email</div>
                    <div className="text-sm md:text-lg font-semibold text-white truncate">{modalData.email}</div>
                  </div>
                  <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Source</div>
                    <div className="text-sm md:text-lg font-semibold text-white">{modalData.source}</div>
                  </div>
                </div>

                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Services Required</div>
                  <div className="text-base md:text-xl font-semibold text-white">{modalData.services}</div>
                </div>

                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Referrer Source</div>
                  <div className="text-sm md:text-lg text-white">{modalData.referrer}</div>
                </div>

                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Potential Revenue</div>
                  <div className="text-2xl md:text-3xl font-bold text-green-500">{modalData.revenue}</div>
                </div>

                <div className="p-4 md:p-6 bg-gradient-to-r from-purple-900/30 to-purple-800/30 border border-purple-500/50 rounded-lg text-center">
                  <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">AI Lead Score</div>
                  <div className="text-4xl md:text-6xl font-bold text-purple-400 mb-1 md:mb-2">{modalData.aiScore}</div>
                  <div className="text-base md:text-xl font-semibold text-purple-300">
                    {modalData.aiScore >= 85 ? 'High Potential' : modalData.aiScore >= 70 ? 'Good Prospect' : 'Needs Review'}
                  </div>
                </div>

                <div className="flex gap-2 md:gap-3">
                  <button className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-bold py-2.5 md:py-3 rounded-xl transition-colors text-sm md:text-base">
                    View Campaign
                  </button>
                  <button className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2.5 md:py-3 rounded-xl transition-colors text-sm md:text-base">
                    Assign to Team
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default RealEstateManagingPartnerMarketing;